import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { Tooltip } from '../../../../common/';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'antd';
import { useLogoutMutation } from '../../../../../redux/services/authApi';
import { UserOutlined } from '@ant-design/icons';
import { openModal } from '../../../../../redux/slices/searchModalSlice.jsx';

const TopNavigation = () => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const [logout] = useLogoutMutation();
  const auth = useSelector((state) => state.auth);

  useEffect(() => {
    if (location && location.pathname === routes.logout) {
      logout();
    }
  }, [logout, location]);

  // ------ SEARCH -------
  // Redux for opening search modal
  const dispatch = useDispatch();
  // Keyboard shortcut hints are documented below in the effect handler.

  // Feature flag to temporarily disable keyboard shortcuts for search
  const ENABLE_SEARCH_SHORTCUTS = false;

  /**
   * Keyboard shortcuts for opening the global search modal:
   * - Double-tap Left Shift within 500ms (ShiftLeft pressed twice) -> open search
   *   We track the timestamp of the last non-repeated ShiftLeft and compare it.
   * - Cmd+K on macOS or Ctrl+K on other platforms -> open search
   *   Default browser action (Find in page) is prevented.
   */
  useEffect(() => {
    // Temporarily disabled
    if (!ENABLE_SEARCH_SHORTCUTS) return;

    let lastLeftShiftTime = 0; // timestamp of last ShiftLeft press
    const doublePressDelay = 500; // max time window between two ShiftLeft presses (ms)

    const handleKeyDown = (event) => {
      // Handle double-tap Left Shift (ignore long-press via event.repeat)
      if (event.code === 'ShiftLeft' && !event.repeat) {
        const now = Date.now();
        if (now - lastLeftShiftTime < doublePressDelay) {
          event.preventDefault();
          dispatch(openModal());
          lastLeftShiftTime = 0; // reset after successful double-tap
        } else {
          lastLeftShiftTime = now; // store time of first tap
        }
      }

      // Handle Cmd+K (mac) or Ctrl+K (others) to open search
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault(); // Prevent browser's built-in page search
        dispatch(openModal());
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dispatch, ENABLE_SEARCH_SHORTCUTS]);
  // --------------------

  return (
    <Row className="top-navigation-container">
      <Col
        style={{ paddingLeft: 12, paddingRight: 12, textAlign: 'left' }}
        span={10}
        sm={{ span: 9 }}
        md={{ span: 8 }}
        lg={{ span: 9 }}
        xl={{ span: 9 }}
        xxl={{ span: 7 }}
      >
        <a
          target="_blank"
          rel="noreferrer"
          href="https://www.facebook.com/FRIuniza"
          aria-label={t('Facebook')}
          className="social-link-btn"
        >
          <Tooltip tooltip={t('Facebook')}>
            <i className="fab fa-facebook-f" />
          </Tooltip>
        </a>
        <span className="mr-1 ml-1">|</span>
        <a
          target="_blank"
          rel="noreferrer"
          href="https://www.instagram.com/budfri.sk"
          aria-label={t('Instagram')}
          className="social-link-btn"
        >
          <Tooltip tooltip={t('Instagram')}>
            <i className="fab fa-instagram" />
          </Tooltip>
        </a>
        <span className="mr-1 ml-1">|</span>
        <a
          target="_blank"
          rel="noreferrer"
          href="https://www.linkedin.com/school/fakulta-riadenia-a-informatiky"
          aria-label={t('LinkedIn')}
          className="social-link-btn"
        >
          <Tooltip tooltip={t('LinkedIn')}>
            <i className="fab fa-linkedin" />
          </Tooltip>
        </a>
        <span className="mr-1 ml-1">|</span>
        <a
          target="_blank"
          rel="noreferrer"
          href="https://www.youtube.com/@budFRIsk"
          aria-label={t('YouTube')}
          className="social-link-btn"
        >
          <Tooltip tooltip={t('YouTube')}>
            <i className="fab fa-youtube" />
          </Tooltip>
        </a>
      </Col>
      <Col
        style={{ paddingLeft: 12, paddingRight: 12, textAlign: 'right' }}
        span={14}
        sm={{ span: 15 }}
        md={{ span: 16 }}
        lg={{ span: 13 }}
        xl={{ span: 13 }}
        xxl={{ span: 15 }}
      >
        <div className={`right-controls-wrapper text-right`}>
          <button
            className="change-language-btn"
            onClick={() => dispatch(openModal())}
          >
            <i className="fa fa-search mr-1" />
            <span
              className={'hide-on-small'}
              style={{ textTransform: 'uppercase' }}
            >
            {t('Hľadať')}
          </span>
          </button>
          <b> |</b>
          <button
            className="change-language-btn mr-1"
            onClick={() => i18n.changeLanguage('sk')}
            aria-label={t('Zmeň jazyk na slovenčinu')}
            title={t('Zmeň jazyk na slovenčinu')}
          >
            <i className="fas fa-language ml-1 mr-1" />
            SK
          </button>
          <button
            className="change-language-btn change-language-link mr-1"
            onClick={() => i18n.changeLanguage('en')}
            aria-label={t('Zmeň jazyk na angličtinu')}
            title={t('Zmeň jazyk na angličtinu')}
          >
            EN
          </button>
          <a
            href="http://www.ukr.budfri.sk/"
            className="change-language-btn mr-1"
            aria-label={t('Stránka v ukrajinskom jazyku')}
            title={t('Stránka v ukrajinskom jazyku')}
          >
            UA
          </a>
          <b> | </b>
          <span className="login-wrapper">
          {auth.isLoggedIn && auth.user ? (
            <span>
              <span>
                <Link
                  to={routes.cmsDashboard}
                  className="account-btn"
                  aria-label={t('Prejsť do administrácie')}
                  title={t('Prejsť do administrácie')}
                >
                  <i className="fas fa-tools mr-1 ml-1" />
                  <span className={'hide-on-small'}>
                    {t('administrácia')}
                  </span>
                </Link>
              </span>
              <span className="mr-1 ml-1 hide-on-small">|</span>
              <Link
                to="#" className="account-btn"
                aria-label={t('Odhlásiť sa')}
                title={t('Odhlásiť sa')}
              >
                <span onClick={() => logout()}>
                  <i className="fas fa-sign-out-alt mr-1 ml-1" />
                  <span className={'hide-on-small'}>{t('Odhlásiť sa')}</span>
                </span>
              </Link>
            </span>
          ) : (
            <NavLink
              to={routes.loginPage} className="account-btn"
              aria-label={t('Prihlásenie')}
              title={t('Prihlásenie')}
            >
              <UserOutlined style={{ marginRight: '8px' }} />
              <span className={'hide-on-small'}>{t('Prihlásenie')}</span>
            </NavLink>
          )}
        </span>
        </div>
      </Col>
    </Row>
  );
};

TopNavigation.propTypes = {};

export default TopNavigation;
